import * as THREE from 'three';

/**
 * Exponential low-pass smoothing factor for a given cutoff frequency and timestep.
 * Higher cutoff (or larger dt) → alpha closer to 1 → less smoothing.
 */
function smoothingAlpha(cutoffHz: number, dtSeconds: number): number {
  const tau = 1 / (2 * Math.PI * cutoffHz);
  return 1 / (1 + tau / dtSeconds);
}

export type OneEuroConfig = {
  /** Baseline cutoff (Hz). Lower → more smoothing when the signal is (near-)still. */
  minCutoffHz: number;
  /** Speed coefficient. Higher → cutoff rises faster with speed → less lag on fast motion. */
  beta: number;
  /** Cutoff (Hz) for the low-pass applied to the derivative that drives the adaptive cutoff. */
  dCutoffHz: number;
};

/**
 * 1€ filter (Casiez, Roussel & Vogel, CHI 2012) for a 3-vector signal.
 *
 * Adaptive low-pass: smooths jitter heavily when the signal moves slowly (guard, windup
 * apex) and opens up — minimal lag — when it moves fast (the cut itself). Replaces a fixed
 * N-frame mean, which either lags fast cuts or under-smooths the guard, never both right.
 *
 * Stateful: feed successive samples via `filter`. Not pure.
 */
/** Scratch for the per-sample derivative; transient within `filter`, never retained. */
const _dx = new THREE.Vector3();

export class OneEuroVec3 {
  private xPrev: THREE.Vector3 | null = null;
  private readonly dxPrev = new THREE.Vector3();

  constructor(private readonly cfg: OneEuroConfig) {}

  reset(): void {
    this.xPrev = null;
    this.dxPrev.set(0, 0, 0);
  }

  /**
   * Filter one sample. `dtSeconds` must be > 0. Returns an internally-reused vector that
   * stays valid only until the next `filter` call — copy it if you need to retain it.
   * The first sample (or any non-positive dt) passes through unchanged and seeds the state.
   */
  filter(x: THREE.Vector3, dtSeconds: number): THREE.Vector3 {
    if (this.xPrev === null || !(dtSeconds > 0)) {
      (this.xPrev ??= new THREE.Vector3()).copy(x);
      this.dxPrev.set(0, 0, 0);
      return this.xPrev;
    }

    // Low-passed derivative drives the adaptive cutoff (faster signal → higher cutoff).
    _dx.copy(x).sub(this.xPrev).divideScalar(dtSeconds);
    const aD = smoothingAlpha(this.cfg.dCutoffHz, dtSeconds);
    this.dxPrev.lerp(_dx, aD);

    const cutoff = this.cfg.minCutoffHz + this.cfg.beta * this.dxPrev.length();
    const a = smoothingAlpha(cutoff, dtSeconds);
    this.xPrev.lerp(x, a);
    return this.xPrev;
  }
}
